import {
  Button,
  ButtonSize,
  ButtonStyle,
  enumKeyByValue,
  enumToOptions,
  FontSize,
  IImageUploadResponse,
  ImageType,
  IPixelAspect,
  RichField,
  SelectField,
  stringToOption,
  UploadImageField,
} from '@one-tree/library'
import React, { ReactElement, useState } from 'react'
import styled from 'styled-components'
import { uploadImage } from '../../../helpers/APIhelper'
import { Alignment } from '../../../types/orgTypes'
import { GOOGLE_FONTS } from './GoogleFonts'

export function HostedFontFamily(props: {
  value?: string
  onChange: (value?: string) => void
}): ReactElement {
  const { value, onChange } = props
  const [loading, setLoading] = useState(false)

  const areFontsLoaded = (): boolean => GOOGLE_FONTS.every((font) => {
    const href = `https://fonts.googleapis.com/css2?family=${font.replace(/ /g, '+')}&display=swap`
    return document.querySelector(`link[href="${href}"]`) !== null
  })

  const [fontsLoaded, setFontsLoaded] = useState(areFontsLoaded())

  const loadFonts = (): void => {
    // Check if this component's local state thinks fonts are loaded
    if (fontsLoaded) return
    // If not, check if another instance has loaded them already
    if (areFontsLoaded()) {
      setFontsLoaded(true)
      return
    }

    setLoading(true)

    const loadFont = (font: string): Promise<void> => new Promise((resolve) => {
      const link = document.createElement('link')
      link.href = `https://fonts.googleapis.com/css2?family=${font.replace(/ /g, '+')}&display=swap`
      link.rel = 'stylesheet'
      link.onload = (): void => resolve()
      link.onerror = (): void => resolve()
      document.head.appendChild(link)
    })

    Promise.all(GOOGLE_FONTS.map(loadFont)).then(() => {
      setLoading(false)
      setFontsLoaded(true)
    })
  }

  const options = GOOGLE_FONTS.map((font) => ({
    value: font,
    label: <span style={{ fontFamily: font, fontSize: '1.6em' }}>{font}</span>,
  }))

  return (
    <button
      type="button"
      style={{ all: 'unset' }}
      onClick={!fontsLoaded ? loadFonts : undefined}
    >
      <SelectField
        label="Font family"
        placeholder={loading ? 'Loading...' : 'Select font family...'}
        options={options}
        value={
          fontsLoaded && value
            ? {
              value,
              label: <span style={{ fontFamily: value }}>{value}</span>,
            }
            : stringToOption(value || '')
        }
        onChange={(option): void => onChange(option?.value)}
        listMaxHeight={500}
      />
    </button>
  )
}

export function HostedAlignment(props: {
  value?: Alignment
  onChange: (value?: Alignment) => void
}): ReactElement {
  const { value, onChange } = props

  return (
    <SelectField
      label="Alignment"
      placeholder="Select alignment..."
      options={enumToOptions(Alignment)}
      value={
        value ? { value, label: enumKeyByValue(Alignment, value) } : undefined
      }
      onChange={(option): void => onChange(option?.value)}
    />
  )
}

export function HostedFontSize(props: {
  value?: FontSize
  onChange: (value?: FontSize) => void
}): ReactElement {
  const { value, onChange } = props
  const fontSizes = enumToOptions(FontSize)

  return (
    <SelectField
      label="Font size"
      placeholder="Select font size..."
      options={fontSizes}
      value={
        value ? { value, label: enumKeyByValue(FontSize, value) } : undefined
      }
      onChange={(option): void => onChange(option?.value)}
    />
  )
}

const CustomUploadImage = styled(UploadImageField)`
  width: 400px;
`
export function HostedImageUpload(props: {
  label: string
  imageType: ImageType
  imageFile?: string | null
  subtitle: string
  cropPixelAspect?: { x: number; y: number }
  onSuccess: (response: IImageUploadResponse) => void
  onRemove?: () => void
  minimumAspect?: IPixelAspect
  canCrop?: boolean
  noResizeOrConvert?: true
}): ReactElement {
  const {
    label,
    imageType,
    imageFile,
    subtitle,
    cropPixelAspect,
    onSuccess,
    onRemove,
    minimumAspect,
    canCrop,
    noResizeOrConvert,
  } = props

  return (
    <div>
      <CustomUploadImage
        label={label}
        imageType={imageType}
        previewUrl={imageFile || ''}
        subtitle={subtitle}
        cropPixelAspect={cropPixelAspect}
        upload={uploadImage}
        onSuccess={onSuccess}
        minimumAspect={minimumAspect}
        canCrop={canCrop}
        noResizeOrConvert={noResizeOrConvert}
      />
      {imageFile && (
        <Button
          style={{ marginTop: '6px' }}
          buttonStyle={ButtonStyle.Secondary}
          buttonSize={ButtonSize.Mini}
          onClick={onRemove}
          showConfirmation={true}
        >
          Remove image
        </Button>
      )}
    </div>
  )
}

const StyledRichField = styled(RichField)`
  width: 100%;
  max-width: 580px;
`
export function HostedTextArea(props: {
  value?: string
  onChange: (value?: string) => void
}): ReactElement {
  const { value, onChange } = props

  return (
    <StyledRichField
      label="Text"
      placeholder="Enter text..."
      value={value}
      onChange={onChange}
      hasFontSize={true}
    />
  )
}
